/**
 * BreathingOrb
 *
 * The core visual for the Live Conversation screen.
 * Three nested circles of the primary color pulse on opposite phases so
 * the orb looks like it's gently breathing:
 *   - idle       → slow ambient pulse
 *   - listening  → faster, stronger pulse
 *   - speaking   → warm secondary color shimmer (AI talking)
 *   - thinking   → softer rotation (AI working)
 *
 * Built with Framer Motion. No canvas/waveforms — matches the "organic,
 * calm" design language from /app/design_guidelines.json.
 */
import { motion } from 'framer-motion';

const STATES = {
  idle:      { color: '#E5DACE', glow: 'rgba(198,93,71,0.15)', speed: 3.2, amp: 0.06 },
  listening: { color: '#C65D47', glow: 'rgba(198,93,71,0.45)', speed: 1.3, amp: 0.16 },
  speaking:  { color: '#F3C65B', glow: 'rgba(243,198,91,0.45)', speed: 1.8, amp: 0.12 },
  thinking:  { color: '#EBE9E4', glow: 'rgba(122,117,109,0.25)', speed: 2.4, amp: 0.05 },
};

export default function BreathingOrb({ state = 'idle', size = 220, label, sublabel, level = 0 }) {
  const cfg = STATES[state] || STATES.idle;

  // When we have live amplitude (e.g. while listening), bump the core
  // scale to follow the speaker's voice in real time. Clamped so the
  // pulse never looks manic.
  const liveScale = state === 'listening' ? 1 + Math.min(0.22, level * 0.55) : 1;

  return (
    <div
      className="relative flex flex-col items-center justify-center"
      style={{ width: size, height: size }}
      data-testid={`orb-${state}`}
    >
      {/* Outer glow */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{ background: cfg.glow, filter: 'blur(18px)' }}
        animate={{ scale: [1, 1 + cfg.amp * 2, 1] }}
        transition={{ duration: cfg.speed, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Middle ring */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: size * 0.12,
          background: cfg.color,
          opacity: 0.55,
        }}
        animate={{ scale: [1, 1 + cfg.amp, 1] }}
        transition={{ duration: cfg.speed * 0.85, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Core (reacts to live audio level when available) */}
      <motion.div
        className="absolute rounded-full"
        style={{
          inset: size * 0.25,
          background: cfg.color,
          boxShadow: `0 10px 30px -8px ${cfg.glow}`,
          transform: `scale(${liveScale})`,
          transition: 'transform 80ms linear',
        }}
        animate={state === 'listening' ? undefined : { scale: [1, 1 + cfg.amp * 0.6, 1] }}
        transition={{ duration: cfg.speed * 0.7, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* Label */}
      <div className="relative z-10 text-center pointer-events-none select-none">
        <div className="text-sm font-semibold text-[var(--lf-text)]">{label}</div>
        {sublabel ? (
          <div className="text-xs text-[var(--lf-text-muted)] mt-0.5">{sublabel}</div>
        ) : null}
      </div>
    </div>
  );
}
